import React, { FC } from 'react';
import cx from 'classnames';

import FooterLinks from './FooterLinks';

import styles from './Footer.css';
import { Experiment, Navigation, NavigationClickHandler } from '../types';
import { TranslationProvider } from '../utils/translations';
import OrganizationSchema from './OrganizationSchema';
import Copyright from './Copyright';
import AppStoreIcons from './AppStoreIcons';
import LanguageSwitcher from './LanguageSwitcher';
import SocialMediaIcons from './SocialMediaIcons';
import { ExperimentProvider } from '../utils/experiments';

type Props = {
  /**
   * Custom styles
   * - root: footer wrapper style
   * - links > root: link sections wrapper style
   * - links > header: link section header style
   * - links > item: link item style
   * - appStores > root: app stores wrapper style
   * - appStores > item: app store item style
   * - socialMedia > root: social media wrapper style
   * - socialMedia > item: social media item style
   */
  classNames?: {
    root?: string;
    links?: {
      root?: string;
      section?: string;
      header?: string;
      item?: string;
    };
    appStores?: {
      root?: string;
      item?: string;
    };
    socialMedia?: {
      root?: string;
      item?: string;
    };
  };

  /**
   * Current a/b tests, e.g. by optimizely
   */
  experiments?: Experiment[];

  /**
   * Translations for texts and labels
   */
  messages: Record<string, string>;

  /**
   * Current locale
   */
  locale: string;

  /**
   * Locale switch handler
   */
  onLocaleChange: (locale: string) => void;

  /**
   * Toggle the language switcher visibility. Defaults to true
   */
  showLanguageSwitcher?: boolean;

  /**
   * Footer navigation entries
   */
  navigation?: Navigation;

  /**
   * navigation click handler (link, button, etc.)
   * @param name  the name of the navigation item
   * @param details additional data defined by the clicked item
   * @param event click event
   * @param url target
   */
  onNavigationClick?: NavigationClickHandler;

  /**
   * Origin url for public domain, if not provided, default will be used (DEFAULT_PUBLIC_DOMAIN_URL)
   */
  publicDomainUrl?: string;
};

const Footer: FC<Props> = ({
  classNames,
  locale,
  messages,
  experiments,
  navigation,
  onNavigationClick,
  onLocaleChange,
  publicDomainUrl,
  showLanguageSwitcher = true
}) => (
  <TranslationProvider locale={locale} messages={messages}>
    <ExperimentProvider values={experiments}>
      <footer
        className={cx(styles.component, classNames?.root)}
        data-testid="footer"
      >
        <FooterLinks
          onClick={onNavigationClick}
          sections={navigation?.links}
          classNames={classNames?.links}
        />
        <hr className={styles.line} />
        <div className={styles.subFooterContainer}>
          <div className={styles.appStoreAndCreditArea}>
            <Copyright />
            <AppStoreIcons
              items={navigation?.appStores}
              classNames={classNames?.appStores}
              onClick={onNavigationClick}
            />
          </div>
          <div className={styles.languageSwitcherAndSocialMediaArea}>
            {showLanguageSwitcher && (
              <LanguageSwitcher
                locale={locale}
                onLocaleChange={onLocaleChange}
              />
            )}
            <SocialMediaIcons
              items={navigation?.socialMedia}
              onClick={onNavigationClick}
              classNames={classNames?.socialMedia}
            />
          </div>
        </div>
      </footer>
      <OrganizationSchema
        socialMediaUrls={navigation?.socialMedia?.map((item) => item.url)}
        publicDomainUrl={publicDomainUrl}
      />
    </ExperimentProvider>
  </TranslationProvider>
);

export default Footer;
